import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null); // null=checking, false=anon, object=authed
  const [restaurantId, setRestaurantId] = useState(localStorage.getItem("supplivo_restaurant") || "");

  const applySession = useCallback((data) => {
    if (data?.access_token) localStorage.setItem("supplivo_token", data.access_token);
    setSession({ user: data.user, organisation: data.organisation, restaurants: data.restaurants || [], features: data.features || {} });
    const rid = localStorage.getItem("supplivo_restaurant");
    const valid = (data.restaurants || []).some((r) => r.id === rid);
    const next = valid ? rid : data.restaurants?.[0]?.id || "";
    setRestaurantId(next);
    localStorage.setItem("supplivo_restaurant", next);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      applySession(data);
    } catch {
      localStorage.removeItem("supplivo_token");
      setSession(false);
    }
  }, [applySession]);

  useEffect(() => {
    // Returning from Google OAuth: AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setSession(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("supplivo_token");
    setSession(false);
  }, []);

  const selectRestaurant = useCallback((id) => {
    setRestaurantId(id);
    localStorage.setItem("supplivo_restaurant", id);
  }, []);

  const value = useMemo(() => ({
    session, user: session?.user, organisation: session?.organisation, restaurants: session?.restaurants || [], features: session?.features || {},
    isOwner: session?.user?.role !== "staff",
    isTrueOwner: session?.user?.role === "owner",
    can: (perm) => session?.user?.role === "owner" || (session?.user?.role === "admin" && session?.user?.permissions?.[perm] !== false),
    restaurantId, selectRestaurant, applySession, refresh: checkAuth, logout,
    updateUser: (u) => setSession((s) => (s ? { ...s, user: u } : s)),
  }), [session, restaurantId, selectRestaurant, applySession, checkAuth, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
